import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { UprightCap } from "../../../../../http/border/types/uprightCaps";
import { type UpdateUprightBody, type Upright } from "../../../../../http/border/types/uprights";
import { UprightType } from "../../../../../http/border/types/uprightTypes";
import { useUprightCapsApi } from "../../../../../http/border/useUprightCaps";
import { useUprightsApi } from "../../../../../http/border/useUprights";
import { useUprightTypesApi } from "../../../../../http/border/useUprightTypes";
import { SearchAdminItem } from "../../../../../http/types/items";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Numeric from "../../../../ui/Numeric";
import { CustomAsyncSelect, CustomSelect } from "../../../../ui/select/CustomSelect";
import Switch from "../../../../ui/Switch";

interface UprightFormBody {
  item: SearchAdminItem | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  height: number;
  type: UprightType | null;
  upperHoleDiameter: number;
  lowerHoleDiameter?: number;
  isReinforced: boolean;
  uprightCap?: UprightCap | null;
}

interface UprightFormProps {
  upright?: Upright;
  onClose: () => void;
}

const UprightForm: FC<UprightFormProps> = ({ onClose, upright }) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const [defaultOptions, setDefaultOptions] = useState<SearchAdminItem[]>([]);
  const [defaultUprightCapOptions, setDefaultUprightCapOptions] = useState<UprightCap[]>([]);

  const itemsApi = useItemsApi();
  const uprightCapsApi = useUprightCapsApi();
  const uprightTypesApi = useUprightTypesApi();
  const uprightsApi = useUprightsApi();
  const uprightTypesAborter = useRef(new AbortController());
  const uprightTypesResult = uprightTypesApi.useAdminUprightTypes({ limit: 100 }, uprightTypesAborter.current.signal);

  const defaultValues = {
    item: upright?.item ?? null,
    order: upright?.order ?? 50,
    visibility: upright?.visibility ?? "hidden",
    height: upright?.height,
    type: upright?.type ?? null,
    isReinforced: upright?.isReinforced,
    lowerHoleDiameter: upright?.lowerHoleDiameter,
    upperHoleDiameter: upright?.upperHoleDiameter,
    uprightCap: upright?.uprightCap ?? null,
  };

  return (
    <Form<UprightFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      secondaryButton={
        <Button buttonProps={{ onClick: onClose }} type="secondary">
          {i18n.translation.common.cancel}
        </Button>
      }
      onSubmit={() => async (formData) => {
        setIsSubmitting(true);

        const common: UpdateUprightBody = {
          order: formData.order!,
          visibility: formData.visibility,
          height: formData.height ?? 0,
          typeId: formData.type?.id ?? "",
          isReinforced: formData.isReinforced,
          lowerHoleDiameter: formData.lowerHoleDiameter,
          upperHoleDiameter: formData.upperHoleDiameter,
          uprightCapId: formData.uprightCap?.item.id,
        };
        if (upright)
          try {
            await uprightsApi.updateAdminUpright(upright.item.id, common);
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
        else
          try {
            await uprightsApi.insertAdminUpright({
              ...common,
              itemId: formData.item?.id ?? "",
            });
            onClose();
          } catch (err) {
            dispatch(showErrorNotification(i18n, err));
          } finally {
            setIsSubmitting(false);
          }
      }}
    >
      {({ control, setValue, watch, reset }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [upright]);

        const isReinforced = watch("isReinforced");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <>
                    <FormField label={i18n.translation.items.singular} error={fieldState.error}>
                      {({ labelId }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions(aborter, searchTerm, itemsApi.getAdminItems);

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultOptions);
                        }, []);

                        return (
                          <CustomAsyncSelect
                            {...field}
                            inputId={labelId}
                            defaultOptions={defaultOptions}
                            loadOptions={loadOptions}
                            isClearable
                            getOptionLabel={itemLabel}
                            isDisabled={!!upright?.item.id}
                            getOptionValue={(x) => x.id}
                            isInvalid={fieldState.invalid}
                            onChange={(value) => setValue("item", value ?? null)}
                          />
                        );
                      }}
                    </FormField>
                  </>
                );
              }}
            />
            <Controller
              name="uprightCap"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.uprightCaps.singular} error={fieldState.error} isOptional>
                  {({ labelId }) => {
                    const searchTerm = useRef("");
                    const aborter = useRef(new AbortController());

                    const loadOptions = createLoadOptions(aborter, searchTerm, uprightCapsApi.getAdminUprightCaps);

                    useEffect(() => {
                      loadOptions(searchTerm.current).then(setDefaultUprightCapOptions);
                    }, []);

                    return (
                      <CustomAsyncSelect
                        {...field}
                        inputId={labelId}
                        defaultOptions={defaultUprightCapOptions}
                        loadOptions={loadOptions}
                        isClearable
                        getOptionLabel={(x) => itemLabel(x.item)}
                        getOptionValue={(x) => x.item.id}
                        isInvalid={fieldState.invalid}
                        onChange={(value) => setValue("uprightCap", value ?? null)}
                      />
                    );
                  }}
                </FormField>
              )}
            />
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <CustomSelect
                      {...field}
                      inputId={labelId}
                      options={uprightTypesResult.data?.items ?? []}
                      placeholder={`${i18n.translation.common.select}...`}
                      isDisabled={!!uprightTypesResult.error || isSubmitting}
                      isLoading={uprightTypesResult.isLoading}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="height"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.height} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("height", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="upperHoleDiameter"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.upperHoleDiameter} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("upperHoleDiameter", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="lowerHoleDiameter"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.lowerHoleDiameter} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      suffix=" mm"
                      allowNegative={false}
                      onBlur={(value) => value && setValue("lowerHoleDiameter", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.common.isReinforced} isOptional>
              {({ labelId }) => (
                <Switch id={labelId} checked={isReinforced} onChange={(checked) => setValue("isReinforced", checked)} />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UprightForm;
